import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useNextStep } from '../../app/navigation';
import { Screen } from '../../chrome';
import { AddButton, Button, Hint, Icon, Question, Sub } from '../../components';
import { useT } from '../../i18n';
import type { Airport } from '../../lib/api';
import { startNewCheck, useTrip, type TripDraft } from '../../lib/funnel';
import { useTrack } from '../../lib/analytics';
import { AirportField } from './AirportField';
import { useStage } from './trip';
import './S1.css';

/**
 * After an airport is picked with Enter (w6-04 N6: focus fell to <body>, and the next Tab
 * started at the top of the page): the next empty airport field below, else any empty one,
 * else the dock's Continue; failing all that, the input it was picked in. After the render
 * that fills the field and enables Continue.
 */
function focusAfterPick(input: HTMLInputElement) {
  requestAnimationFrame(() => {
    const fields = [
      ...document.querySelectorAll<HTMLInputElement>('[data-screen="S1"] input[role="combobox"]'),
    ];
    const empty = fields.filter((f) => f !== input && !f.value);
    const below = empty.find((f) => input.compareDocumentPosition(f) & Node.DOCUMENT_POSITION_FOLLOWING);
    const cta = document.querySelector<HTMLButtonElement>('[data-testid="dock"] .btn:not(:disabled)');
    (below ?? empty[0] ?? cta ?? input).focus();
  });
}

/** Where they changed planes: at most three (ux-flow.md S1). */
const MAX_CONNECTIONS = 3;
const ENTRIES = ['delayed', 'cancelled', 'other'] as const;

type Conn = { key: number; airport: Airport | null };
let connKey = 0;

/**
 * S1 · Where did you fly? Departure, optional connections, destination. The homepage's
 * "What went wrong?" arrives as `?type=` and starts a new check (ux-flow.md §1).
 */
export default function ScreenS1() {
  const t = useT();
  const track = useTrack();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { enquiry, trip } = useStage('S1');
  const { save, setEntry, isSaving } = useTrip();
  const { goNext } = useNextStep();

  // ?type= from the homepage card: a new check with that entry, then drop the parameter
  // so a reload doesn't start yet another one.
  const type = params.get('type');
  useEffect(() => {
    if (type === null) return;
    startNewCheck();
    if ((ENTRIES as readonly string[]).includes(type)) setEntry(type as NonNullable<TripDraft['entry']>);
    const rest = new URLSearchParams(params);
    rest.delete('type');
    const qs = rest.toString();
    navigate({ search: qs ? `?${qs}` : '' }, { replace: true });
    // Only when ?type= appears; setEntry and navigate are stable enough for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const [origin, setOrigin] = useState<Airport | null>(trip.origin);
  const [destination, setDestination] = useState<Airport | null>(trip.destination);
  const [conns, setConns] = useState<Conn[]>(() =>
    trip.connections.map((airport) => ({ key: ++connKey, airport })),
  );
  const [error, setError] = useState<string | null>(null);

  // The trip arrives after the first render when the enquiry loads (a scan, a reload).
  const tripKey = JSON.stringify([
    trip.origin?.iata,
    trip.destination?.iata,
    trip.connections.map((c) => c.iata),
  ]);
  const [loadedKey, setLoadedKey] = useState(tripKey);
  if (tripKey !== loadedKey) {
    setLoadedKey(tripKey);
    setOrigin(trip.origin);
    setDestination(trip.destination);
    setConns(trip.connections.map((airport) => ({ key: ++connKey, airport })));
  }

  // A partial boarding-pass scan lands here with what it read (ux-flow.md S0b).
  const partialScan = enquiry?.entry === 'scan' && !enquiry.trip.date && !!enquiry.trip.origin;
  const ready = !!(origin && destination);

  function addConnection() {
    if (conns.length >= MAX_CONNECTIONS) return;
    setConns([...conns, { key: ++connKey, airport: null }]);
    track('s1_connection_added', { count: conns.length + 1 });
  }

  function setConn(key: number, airport: Airport | null) {
    setConns((cs) => cs.map((c) => (c.key === key ? { ...c, airport } : c)));
    setError(null);
  }

  async function onContinue() {
    if (!origin || !destination) return;
    const connections = conns.map((c) => c.airport).filter((a): a is Airport => !!a);
    const chain = [origin, ...connections, destination].map((a) => a.iata);
    if (chain.some((code, i) => i > 0 && code === chain[i - 1])) {
      setError(t('S1.sameAirport'));
      return;
    }
    track('s1_route_set', { connections: connections.length });
    const e = await save({ origin, destination, connections });
    await goNext(e ?? undefined);
  }

  const toLabel = conns.length ? t('S1.to.labelFinal') : t('S1.to.label');
  return (
    <Screen
      id="S1"
      title={t('S1.title')}
      dock={
        <Button onClick={onContinue} disabled={!ready || isSaving}>
          {t('common.continue')}
        </Button>
      }
    >
      {partialScan && <Hint warn>{t('S1.scanNotice')}</Hint>}
      <Question>{t('S1.title')}</Question>
      <Sub>{t('S1.sub')}</Sub>

      <AirportField
        key={`from-${loadedKey}`}
        label={t('S1.from.label')}
        placeholder={t('S1.from.placeholder')}
        value={origin}
        onPick={(a) => {
          setOrigin(a);
          setError(null);
        }}
        onClear={() => setOrigin(null)}
        onKeyboardPick={focusAfterPick}
      />

      {conns.length < MAX_CONNECTIONS && (
        <AddButton onClick={addConnection}>{t('S1.addConnection')}</AddButton>
      )}
      {conns.map((c, i) => (
        <AirportField
          key={c.key}
          label={t('S1.via.label')}
          placeholder={t('S1.via.placeholder')}
          value={c.airport}
          autoFocus={!c.airport}
          onPick={(a) => setConn(c.key, a)}
          onClear={() => setConn(c.key, null)}
          onKeyboardPick={focusAfterPick}
          trailing={
            <button
              type="button"
              className="field__trail"
              aria-label={t('S1.via.remove', { n: i + 1 })}
              onClick={() => setConns((cs) => cs.filter((x) => x.key !== c.key))}
            >
              <Icon name="x" />
            </button>
          }
        />
      ))}

      <AirportField
        key={`to-${loadedKey}`}
        label={toLabel}
        placeholder={t('S1.to.placeholder')}
        value={destination}
        error={error}
        onPick={(a) => {
          setDestination(a);
          setError(null);
          // Nothing says the CTA is down there (prototype defect 4): bring it up.
          requestAnimationFrame(() =>
            document
              .querySelector('[data-testid="dock"] .btn')
              ?.scrollIntoView?.({ block: 'end', behavior: 'smooth' }),
          );
        }}
        onClear={() => setDestination(null)}
        onKeyboardPick={focusAfterPick}
      />

      <Hint>{t('S1.hint')}</Hint>
    </Screen>
  );
}
